// Main call routing store using modular structure
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

// Import types and helper functions
import type {
    CallRoutingConfiguration,
    CallRoutingState,
    GoodbyeMessage,
    GreetingMessage
} from './call-routing/types'

import {
    fetchCallRoutingConfiguration,
    saveCallRoutingConfiguration
} from './call-routing/api'

import {
    createDefaultElement,
    deleteEntryByIndex,
    findDefaultElement,
    getExceptionElements,
    showToastDeleteCallRoutingException,
    showToastDeleteCallRoutingObject,
    showToastErrorModifyCallRoutingObject,
    showToastNewCallRoutingObject,
    showToastUpdateCallRoutingExceptionTime,
    showToastUpdateCallRoutingObject,
    showToastUpdateGoodbyeMessage,
    showToastUpdateGreetingMessage,
    updateEntryAtIndex
} from './call-routing/helpers'

export const useCallRoutingStore = create<CallRoutingState>()(
    persist(
        immer((set, get) => ({
            // Initial state
            isUpdating: false,
            callRoutingConfigurationObject: {
                selectionElements: []
            },

            // Initialize configuration from API
            initCallRoutingConfiguration: async () => {
                try {
                    set((state) => {
                        state.isUpdating = true
                    })

                    const configuration = await fetchCallRoutingConfiguration()

                    set((state) => {
                        state.callRoutingConfigurationObject = configuration
                        state.isUpdating = false
                    })
                } catch (error) {
                    console.error('Error initializing call routing configuration:', error)
                    set((state) => {
                        state.isUpdating = false
                    })
                }
            },

            // Set call routing object
            setCallRoutingObject: (elements: CallRoutingConfiguration) => {
                set((state) => {
                    state.callRoutingConfigurationObject = elements
                })
            },

            // Get exception configuration list
            getExceptionConfigurationList: () => {
                const state = get()
                return getExceptionElements(state.callRoutingConfigurationObject.selectionElements)
            },

            // Get default greeting message
            getDefaultGreetingMessageObject: (): GreetingMessage => {
                const state = get()
                const defaultElement = findDefaultElement(state.callRoutingConfigurationObject.selectionElements)

                return {
                    message: defaultElement?.utteranceToSpeak || '',
                    language: defaultElement?.utteranceLanguage || 'en'
                }
            },

            // Get default goodbye message
            getDefaultGoodbyeMessageObject: (): GoodbyeMessage => {
                const state = get()
                const defaultElement = findDefaultElement(state.callRoutingConfigurationObject.selectionElements)

                return {
                    message: defaultElement?.goodbyeUtteranceToSpeak || '',
                    language: defaultElement?.goodbyeUtteranceLanguage || 'en'
                }
            },

            // Set updating state
            setIsUpdating: (isUpdating: boolean) => {
                set((state) => {
                    state.isUpdating = isUpdating
                })
            },

            // Add new call routing object
            addNewCallRoutingObject: async (newObject: any) => {
                try {
                    set((state) => {
                        state.callRoutingConfigurationObject.selectionElements.push(newObject)
                    })

                    const state = get()
                    const success = await saveCallRoutingConfiguration(state.callRoutingConfigurationObject)

                    if (success) {
                        showToastNewCallRoutingObject()
                    } else {
                        showToastErrorModifyCallRoutingObject()
                    }
                } catch (error) {
                    console.error('Error adding new call routing object:', error)
                    showToastErrorModifyCallRoutingObject()
                }
            },

            // Delete call routing object
            deleteCallRoutingObject: async (indexToDelete: number) => {
                try {
                    set((state) => {
                        state.callRoutingConfigurationObject.selectionElements =
                            deleteEntryByIndex(state.callRoutingConfigurationObject.selectionElements, indexToDelete)
                    })

                    const state = get()
                    const success = await saveCallRoutingConfiguration(state.callRoutingConfigurationObject)

                    if (success) {
                        showToastDeleteCallRoutingObject()
                    } else {
                        showToastErrorModifyCallRoutingObject()
                    }
                } catch (error) {
                    console.error('Error deleting call routing object:', error)
                    showToastErrorModifyCallRoutingObject()
                }
            },

            // Delete call routing exception
            deleteCallRoutingException: async (exceptionIndexToDelete: number) => {
                try {
                    const state = get()
                    const exceptions = getExceptionElements(state.callRoutingConfigurationObject.selectionElements)

                    if (exceptionIndexToDelete < exceptions.length) {
                        // Find the actual index in the full array
                        const elementToDelete = exceptions[exceptionIndexToDelete]
                        const actualIndex = state.callRoutingConfigurationObject.selectionElements.indexOf(elementToDelete)

                        if (actualIndex !== -1) {
                            set((state) => {
                                state.callRoutingConfigurationObject.selectionElements =
                                    deleteEntryByIndex(state.callRoutingConfigurationObject.selectionElements, actualIndex)
                            })

                            const newState = get()
                            const success = await saveCallRoutingConfiguration(newState.callRoutingConfigurationObject)

                            if (success) {
                                showToastDeleteCallRoutingException()
                            } else {
                                showToastErrorModifyCallRoutingObject()
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error deleting call routing exception:', error)
                    showToastErrorModifyCallRoutingObject()
                }
            },

            // Set greeting message
            setGreetingMessage: async (newMessage: string, newLanguage: string) => {
                try {
                    set((state) => {
                        const defaultElement = findDefaultElement(state.callRoutingConfigurationObject.selectionElements)
                        if (defaultElement) {
                            defaultElement.utteranceToSpeak = newMessage
                            defaultElement.utteranceLanguage = newLanguage
                        } else {
                            // Create default element if it doesn't exist
                            const newDefault = createDefaultElement()
                            newDefault.utteranceToSpeak = newMessage
                            newDefault.utteranceLanguage = newLanguage
                            state.callRoutingConfigurationObject.selectionElements.push(newDefault)
                        }
                    })

                    const state = get()
                    const success = await saveCallRoutingConfiguration(state.callRoutingConfigurationObject)

                    if (success) {
                        showToastUpdateGreetingMessage()
                    } else {
                        showToastErrorModifyCallRoutingObject()
                    }
                } catch (error) {
                    console.error('Error setting greeting message:', error)
                    showToastErrorModifyCallRoutingObject()
                }
            },

            // Set goodbye message
            setGoodbyeMessage: async (newMessage: string, newLanguage: string) => {
                try {
                    set((state) => {
                        const defaultElement = findDefaultElement(state.callRoutingConfigurationObject.selectionElements)
                        if (defaultElement) {
                            defaultElement.goodbyeUtteranceToSpeak = newMessage
                            defaultElement.goodbyeUtteranceLanguage = newLanguage
                        } else {
                            // Create default element if it doesn't exist
                            const newDefault = createDefaultElement()
                            newDefault.goodbyeUtteranceToSpeak = newMessage
                            newDefault.goodbyeUtteranceLanguage = newLanguage
                            state.callRoutingConfigurationObject.selectionElements.push(newDefault)
                        }
                    })

                    const state = get()
                    const success = await saveCallRoutingConfiguration(state.callRoutingConfigurationObject)

                    if (success) {
                        showToastUpdateGoodbyeMessage()
                    } else {
                        showToastErrorModifyCallRoutingObject()
                    }
                } catch (error) {
                    console.error('Error setting goodbye message:', error)
                    showToastErrorModifyCallRoutingObject()
                }
            },

            // Update call routing object
            updateCallRoutingObject: async (indexToUpdate: number, newObject: any) => {
                try {
                    set((state) => {
                        state.callRoutingConfigurationObject.selectionElements =
                            updateEntryAtIndex(state.callRoutingConfigurationObject.selectionElements, indexToUpdate, newObject)
                    })

                    const state = get()
                    const success = await saveCallRoutingConfiguration(state.callRoutingConfigurationObject)

                    if (success) {
                        showToastUpdateCallRoutingObject()
                    } else {
                        showToastErrorModifyCallRoutingObject()
                    }
                } catch (error) {
                    console.error('Error updating call routing object:', error)
                    showToastErrorModifyCallRoutingObject()
                }
            },

            // Update call routing exception time
            updateCallRoutingExceptionTime: async (exceptionIndex: number, newFromTime: string, newToTime: string) => {
                try {
                    const state = get()
                    const exceptions = getExceptionElements(state.callRoutingConfigurationObject.selectionElements)

                    if (exceptionIndex < exceptions.length) {
                        const elementToUpdate = exceptions[exceptionIndex]
                        const actualIndex = state.callRoutingConfigurationObject.selectionElements.indexOf(elementToUpdate)

                        if (actualIndex !== -1) {
                            set((state) => {
                                const element = state.callRoutingConfigurationObject.selectionElements[actualIndex]
                                element.fromTime = newFromTime
                                element.toTime = newToTime
                            })

                            const newState = get()
                            const success = await saveCallRoutingConfiguration(newState.callRoutingConfigurationObject)

                            if (success) {
                                showToastUpdateCallRoutingExceptionTime()
                            } else {
                                showToastErrorModifyCallRoutingObject()
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error updating call routing exception time:', error)
                    showToastErrorModifyCallRoutingObject()
                }
            }
        })),
        {
            name: 'call-routing-store',
            // Only persist the configuration object, not the loading state
            partialize: (state) => ({
                callRoutingConfigurationObject: state.callRoutingConfigurationObject
            })
        }
    )
)